import { findIndex, isEmpty } from 'lodash';
import moment from 'moment';

// Normalize a worker's display name for the customer-facing UI:
// - strip a trailing numeric suffix so duplicate logins collapse (e.g. "tina2" -> "tina")
// - render in ALL CAPS
export const formatWorkerName = (name) => {
  if (!name || typeof name !== 'string') return name;
  return name.replace(/\d+$/, '').trim().toUpperCase();
};

// Check if a worker has the required capabilities to perform a service
export const canWorkerPerformService = (worker, service) => {
  if (!worker || !service) {
    return false;
  }

  // Check each service capability flag
  if (service.booking && !worker.pedicure) {
    return false;
  }
  if (service.acrylic && !worker.acrylic) {
    return false;
  }
  if (service.dip && !worker.dip) {
    return false;
  }
  if (service.wax && !worker.wax) {
    return false;
  }
  if (service.design && !worker.design) {
    return false;
  }
  if (service.serviceHardGel && !worker.hardGel) {
    return false;
  }
  if (service.serviceGel_x && !worker.gel_x) {
    return false;
  }
  if (service.servicePolyGel && !worker.polyGel) {
    return false;
  }
  if (service.toggleOption === "feet" && !worker.pedicure) {
    return false;
  }

  return true;
};

export const setWorker = (element) => {
    if (isEmpty(element.worker) || element?.worker?.value === "any") {
      for (const key in element.totalSlots) {
        if (key !== "any") {
          for (let j = 0; j < element.totalSlots[key].length; j++) {
            if (element.totalSlots[key][j] === element.slot) {
                const index = findIndex(element.workersResponse, (item) => {
                    return item.value === key;
                })
                if (index > -1) {
                    element.worker = element.workersResponse[index];
                } else {
                    element.worker = {
                      name: key,
                      value: key,
                    };
                }
                break;
            }
          }
        }
      }
    }
    let startTime = element.slot;
    for (let j = 0; j < element?.service?.length; j++) {
      // Check if worker is already assigned from Worker component
      if (element.service[j].assignedWorker) {
        element.service[j].worker = element.service[j].assignedWorker;
      } else {
        // Check if the requested worker can perform this service
        const workerCanPerformService = canWorkerPerformService(element.worker, element.service[j]);

        if (workerCanPerformService) {
          // If worker can do the service, assign them
          element.service[j].worker = element.worker;
        } else {
        // If worker cannot do the service, find any available worker for this time slot
        // Flatten all available slots at this time to find available workers
        let ary = element.totalSlots[element.slot];
        let availableWorkers = [];
        if (ary && ary.length) {
          // Flatten the nested arrays to get all available slot options
          ary.forEach(slotArray => {
            if (Array.isArray(slotArray)) {
              availableWorkers = availableWorkers.concat(slotArray);
            }
          });
        }

        // Find an available worker for this specific service time who has the required skills
        let workerFound = false;
        for (let k = 0; k < availableWorkers.length; k++) {
          if (availableWorkers[k].startTime === startTime) {
            const index = findIndex(element.workersResponse, (item) => {
                return item.value === availableWorkers[k].workerId;
            })
            if (index > -1) {
                const candidateWorker = element.workersResponse[index];
                // Validate that this worker can perform the service
                if (canWorkerPerformService(candidateWorker, element.service[j])) {
                  element.service[j].worker = candidateWorker;
                  workerFound = true;
                  break;
                }
            } else {
                element.service[j].worker = { value: availableWorkers[k].workerId };
                workerFound = true;
                break;
            }
          }
        }

          // Fallback: if no specific worker found, find any skilled worker from the worker list
          if (!workerFound && !element.service[j].worker) {
            const skilledWorker = element.workersResponse?.find(worker =>
              canWorkerPerformService(worker, element.service[j])
            );
            element.service[j].worker = skilledWorker || element.workersResponse[0] || element.worker;
          }
        }
      }

      element.service[j].startTime = startTime;
      startTime = moment(startTime, "HH:mm")
        .add(element.service[j].time, "minutes")
        .format("HH:mm");
      element.service[j].endTime = startTime;
    }
    return element;
}

function findMaxRepeatedIndices(arrayOfArrays) {
  let maxCount = 0;
  let bestSlotIndex = 0;
  // Count occurrences of each value and track indices
  arrayOfArrays?.forEach((innerArray, index) => {
    let count = {};
    innerArray.forEach((item) => {
      count[item.workerId] = (count[item.workerId] || 0) + 1;
    });
    for (const value in count) {
      if (count[value] > maxCount) {
        maxCount = count[value];
        bestSlotIndex = index;
      }
    }
    count = {};
  });

  return bestSlotIndex;
}

// Reassign workers to services based on skills and requested worker priority
const reassignWorkersToServices = (slotOption, services, workersResponse, requestedWorker) => {
  // console.log("🔄 Reassigning workers based on skills and requested worker priority...");

  // Build worker map for quick lookup
  const workerMap = {};
  workersResponse.forEach(w => {
    workerMap[w.value] = w;
  });

  const hasRequestedWorker = requestedWorker && requestedWorker.value !== "any" && requestedWorker.value !== "request";

  // Reassign each slot to a skilled worker
  const reassignedSlots = slotOption.map((serviceSlot) => {
    // Find which service this slot is for by matching serviceId
    const service = services.find(s => s.value === serviceSlot.serviceId);
    if (!service) {
      // console.log(`  ⚠️ Warning: No service found for serviceId ${serviceSlot.serviceId}`);
      return serviceSlot;
    }

    // console.log(`  Service: ${service.name} at ${serviceSlot.startTime}-${serviceSlot.endTime}`);

    // Get the worker available in this slot
    const availableWorker = workerMap[serviceSlot.workerId];
    if (!availableWorker) {
      // console.log(`    ⚠️ Worker ${serviceSlot.workerId} not found in worker list`);
      return serviceSlot;
    }

    // console.log(`    Available worker: ${availableWorker.name}`);

    // Priority 1: If requested worker is the available worker AND can do this service
    if (hasRequestedWorker &&
        serviceSlot.workerId === requestedWorker.value &&
        canWorkerPerformService(requestedWorker, service)) {
      // console.log(`    ✅ Assigned to REQUESTED worker: ${requestedWorker.name}`);
      return serviceSlot; // Keep assignment
    }

    // Priority 2: If available worker can do this service
    if (canWorkerPerformService(availableWorker, service)) {
      // console.log(`    ✅ Assigned to skilled worker: ${availableWorker.name}`);
      return serviceSlot; // Keep assignment
    }

    // This shouldn't happen if validation passed
    // console.log(`    ❌ ERROR: Available worker ${availableWorker.name} cannot perform ${service.name}`);
    return serviceSlot;
  });

  // console.log("✅ Worker reassignment complete");
  return reassignedSlots;
};

export const calculateSlots = (element, value) => {
  const { totalSlots, worker, service, workersResponse } = element;
  const consecutiveSlots = totalSlots[value];
  // console.log("🚀 ~ calculateSlots ~ consecutiveSlots:", consecutiveSlots);

  // Check if a specific worker is requested
  const hasRequestedWorker = worker && worker.value !== "any" && worker.value !== "request";

  let selectedSlotOption;

  if (hasRequestedWorker && service && service.length > 0) {
    // console.log("🚀 ~ calculateSlots ~ Requested worker:", worker.name);

    // Determine which services the requested worker can do
    const requestedWorkerCanDoServices = [];
    service.forEach((svc, index) => {
      if (canWorkerPerformService(worker, svc)) {
        requestedWorkerCanDoServices.push(index);
      }
    });

    // console.log("🚀 ~ calculateSlots ~ Requested worker can do service indices:", requestedWorkerCanDoServices);

    // Score each slot option based on requested worker presence
    let bestScore = -1;
    let bestSlotIndex = 0;

    consecutiveSlots.forEach((slotOption, index) => {
      let score = 0;

      // Check if requested worker appears in this slot option
      const requestedWorkerAppears = slotOption.some(slot => slot.workerId === worker.value);
      if (requestedWorkerAppears) {
        score += 100; // Prefer slots where requested worker is available
      }

      // console.log(`  Option ${index}: Score ${score}`);

      if (score > bestScore) {
        bestScore = score;
        bestSlotIndex = index;
      }
    });

    // console.log("🚀 ~ calculateSlots ~ Selected option index:", bestSlotIndex, "with score:", bestScore);
    selectedSlotOption = consecutiveSlots[bestSlotIndex] ? JSON.parse(JSON.stringify(consecutiveSlots[bestSlotIndex])) : [];
  } else {
    // No requested worker - use existing logic (most repeated worker)
    const index = findMaxRepeatedIndices(consecutiveSlots);
    if (index > -1) {
      selectedSlotOption = consecutiveSlots[index] ? JSON.parse(JSON.stringify(consecutiveSlots[index])) : [];
    } else {
      selectedSlotOption = consecutiveSlots[0] ? JSON.parse(JSON.stringify(consecutiveSlots[0])) : [];
    }
  }

  // Validate and assign workers to services based on skills and requested worker priority
  if (selectedSlotOption && selectedSlotOption.length > 0 && service && workersResponse) {
    selectedSlotOption = reassignWorkersToServices(selectedSlotOption, service, workersResponse, worker);
  }

  // selectedSlotOption = selectedSlotOption.map((item) => {
  //   if (item.workerId === worker.value) {
  //     item.request = true;
  //   } else {
  //     item.request = false;
  //   }
  //   return item;
  // })
  return selectedSlotOption;
}

export const calculateAppointments = (appointments) => {
  for (let i = 0; i < appointments.length; i++) {
    appointments[i] = setWorker(appointments[i]);
  }
  return {
    appointments,
  }
};

export const sortTime = (time1, time2) => {
  const timeA = new Date(`1970/01/01 ${time1}`);
  const timeB = new Date(`1970/01/01 ${time2}`);

  // Compare the time objects
  return timeA.getTime() - timeB.getTime();
}

export const compareTime = (slot1, slot2) => {
  return sortTime(slot1.startTime, slot2.startTime);
}

export const parseAvailableSlots = (slots) => {
  const obj = {};
  // The slots API can return a non-array (e.g. {} or an error/empty payload).
  // Guard against it so the booking flow shows "no times" instead of crashing.
  if (!Array.isArray(slots)) {
    return obj;
  }
  for (let consecutiveSlots of slots) {
    if (!Array.isArray(consecutiveSlots) || !consecutiveSlots.length) {
      continue;
    }
    consecutiveSlots = consecutiveSlots.sort(compareTime);
    if (!obj[consecutiveSlots[0].startTime]) {
      obj[consecutiveSlots[0].startTime] = []
    }
    obj[consecutiveSlots[0].startTime] = [...obj[consecutiveSlots[0].startTime], consecutiveSlots];
  }
  return obj;
};

const isValidSlot = (slot, finalSlots) => {
  if (slot?.endsWith("30") || slot?.endsWith("10") || slot?.endsWith("00")) {
    return true;
  }
  const prevSlot = moment(slot, 'HH:mm').subtract(10, 'minutes').format('HH:mm');
  const prevPrevSlot = moment(prevSlot, 'HH:mm').subtract(10, 'minutes').format('HH:mm');
  if (slot?.endsWith("20") || slot?.endsWith("40")) {
    if (finalSlots.indexOf(prevSlot) > -1 || prevPrevSlot > -1) {
      return false;
    } 
  } else if (slot?.endsWith("40")) {
    if (finalSlots.indexOf(prevSlot) > -1) {
      return false;
    } 
  } 
  return true;
}

export const filterTimes = (array) => {
  const filteredArray = [];

  for (let i = 0; i < array.length; i++) {
    isValidSlot(array[i], filteredArray) && filteredArray.push(array[i]);
  }

  return filteredArray;
}